import {
  ShieldCheckIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ServerIcon,
  FingerPrintIcon,
  KeyIcon,
  GlobeAltIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  CodeBracketIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

const sections = [
  {
    id: 'zero-touch',
    number: '1',
    title: 'Zero-Touch, Read-Only Architecture',
    icon: EyeSlashIcon,
    description:
      'CyberActions is engineered as a non-intrusive security observer.',
    items: [
      {
        heading: 'No Write Access',
        text: 'All integrations are restricted to read-only permissions. We cannot modify configurations, delete data, or make changes to your environment.',
      },
      {
        heading: 'Metadata-Only Processing',
        text: 'We analyse security configuration metadata only (e.g., MFA status, sharing settings). We do not access or process:',
        subItems: ['Emails', 'Documents', 'User content'],
      },
      {
        heading: 'Customer-Controlled Remediation',
        text: 'All actions remain under your control. CyberActions provides insights — not enforcement.',
      },
    ],
  },
  {
    id: 'api-integration',
    number: '2',
    title: 'Secure API & Integration Model',
    icon: KeyIcon,
    description:
      'We connect to your environment using industry-standard authentication and official APIs.',
    items: [
      {
        heading: 'OAuth 2.0 Authentication',
        text: 'Secure, token-based access without exposing credentials.',
      },
      {
        heading: 'No Password Access',
        text: 'We never request, see, or store administrative passwords.',
      },
      {
        heading: 'Scoped Permissions',
        text: 'Access is limited strictly to read-only security data.',
      },
      {
        heading: 'Full Customer Control',
        text: 'Access can be revoked instantly via your Microsoft or Google Admin console.',
      },
    ],
  },
  {
    id: 'encryption',
    number: '3',
    title: 'Data Encryption & Protection',
    icon: LockClosedIcon,
    description:
      'All data is protected using industry-standard encryption protocols.',
    items: [
      {
        heading: 'Encryption in Transit',
        text: 'All communications are secured using TLS 1.2+.',
      },
      {
        heading: 'Encryption at Rest',
        text: 'Stored data is encrypted using AES-256.',
      },
      {
        heading: 'Secure Key Management',
        text: 'Encryption keys are managed using secure, industry-standard practices.',
      },
    ],
  },
  {
    id: 'data-residency',
    number: '4',
    title: 'Data Residency & Sovereignty',
    icon: GlobeAltIcon,
    description:
      'CyberActions is designed for UK organisations with strict data requirements.',
    items: [
      {
        heading: 'Primary Data Location',
        text: 'Customer data is hosted within the United Kingdom.',
      },
      {
        heading: 'Controlled Processing',
        text: 'Where third-party services are used, appropriate safeguards are implemented to ensure compliance with UK data protection laws.',
      },
    ],
  },
  {
    id: 'infrastructure',
    number: '5',
    title: 'Infrastructure & Platform Security',
    icon: ServerIcon,
    description:
      'Our platform is built on enterprise-grade cloud infrastructure aligned with global standards.',
    items: [
      {
        heading: 'Certified Infrastructure',
        text: 'Hosted on providers compliant with:',
        subItems: ['ISO 27001', 'SOC 2'],
      },
      {
        heading: 'Environment Isolation',
        text: 'Strong logical separation ensures that customer environments remain fully isolated.',
      },
      {
        heading: 'Least Privilege Access',
        text: 'Internal access is strictly limited based on role and necessity.',
      },
      {
        heading: 'No Standing Access',
        text: 'CyberActions personnel do not have default access to customer metadata.',
      },
    ],
  },
  {
    id: 'monitoring',
    number: '6',
    title: 'Monitoring, Logging & Threat Detection',
    icon: BellAlertIcon,
    description:
      'We continuously monitor our platform to maintain security integrity.',
    items: [
      {
        heading: 'Real-Time Monitoring',
        text: 'Automated systems detect suspicious activity and anomalies.',
      },
      {
        heading: 'Audit Logging',
        text: 'All access and system activity is logged and retained for:',
        subItems: ['Security investigations', 'Compliance requirements'],
      },
      {
        heading: 'Threat Detection',
        text: 'Suspicious login attempts and API misuse are automatically flagged and blocked.',
      },
    ],
  },
  {
    id: 'incident-response',
    number: '7',
    title: 'Incident Response & Breach Management',
    icon: ExclamationTriangleIcon,
    description:
      'CyberActions maintains a structured incident response framework.',
    items: [
      {
        heading: 'Rapid Detection & Response',
        text: 'Security incidents are identified and contained promptly.',
      },
      {
        heading: 'Customer Notification',
        text: 'In the event of a data breach, affected customers are notified in line with UK legal requirements.',
      },
      {
        heading: 'Continuous Improvement',
        text: 'Incidents are reviewed to strengthen future defences.',
      },
    ],
  },
  {
    id: 'devsecops',
    number: '8',
    title: 'Secure Development (DevSecOps)',
    icon: CodeBracketIcon,
    description:
      'Security is embedded throughout our development lifecycle.',
    items: [
      {
        heading: 'Vulnerability Scanning',
        text: 'Continuous scanning of code and dependencies.',
      },
      {
        heading: 'Patch Management',
        text: 'Critical updates applied within 24 hours.',
      },
      {
        heading: 'Security Testing',
        text: 'Regular internal security reviews and testing.',
      },
      {
        heading: 'Code Integrity',
        text: 'Secure coding practices enforced across all development.',
      },
    ],
  },
  {
    id: 'internal-controls',
    number: '9',
    title: 'Internal Security Controls',
    icon: UserGroupIcon,
    description: 'We apply strict security standards internally.',
    items: [
      {
        heading: 'Mandatory Multi-Factor Authentication (MFA)',
        text: 'All administrative access is protected by MFA.',
      },
      {
        heading: 'Access Control Policies',
        text: 'Role-based access with strict enforcement.',
      },
      {
        heading: 'Security Training',
        text: 'Ongoing internal awareness and security practices.',
      },
    ],
  },
  {
    id: 'compliance',
    number: '10',
    title: 'Compliance & Assurance',
    icon: ClipboardDocumentCheckIcon,
    description:
      'CyberActions is built to align with recognised UK cybersecurity frameworks.',
    paragraphs: [
      'Our platform supports organisations working towards:',
    ],
    listItems: [
      'Cyber Essentials',
      'Cyber Essentials Plus',
      'ISO/IEC 27001 principles',
      'UK GDPR compliance',
    ],
    footer:
      'We continuously evolve our controls to meet regulatory and industry expectations.',
  },
  {
    id: 'privacy',
    number: '11',
    title: 'Privacy-First Design',
    icon: FingerPrintIcon,
    description:
      'Security and privacy are deeply integrated into our platform.',
    items: [
      {
        heading: 'No Content Access Guarantee',
        text: 'We never access emails, files, or communications.',
      },
      {
        heading: 'Data Minimisation',
        text: 'Only essential metadata is processed.',
      },
      {
        heading: 'Customer Ownership',
        text: 'You retain full control of your data at all times.',
      },
    ],
  },
];

export default function SecurityPage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Platform Security
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-300">
            At CyberActions, security is not a feature — it is the foundation
            of our platform. Our architecture is designed to deliver actionable
            security insights while maintaining a strict zero-trust,
            minimal-access model.
          </p>
        </div>
      </div>

      {/* Sections */}
      {sections.map((section, index) => (
        <div
          key={section.id}
          className={
            index % 2 === 0
              ? 'bg-slate-50 dark:bg-slate-800/50 py-16'
              : 'bg-white dark:bg-slate-900 py-16'
          }
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
              {/* Section Header */}
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                  <section.icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {section.number}. {section.title}
                </h2>
              </div>

              <p className="mt-4 text-slate-600 dark:text-slate-300">
                {section.description}
              </p>

              {/* Items with bullet points */}
              {section.items && (
                <div className="mt-8 space-y-6">
                  {section.items.map((item) => (
                    <div
                      key={item.heading}
                      className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700"
                    >
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {item.heading}
                      </h3>
                      <p className="mt-2 text-slate-600 dark:text-slate-300">
                        {item.text}
                      </p>
                      {item.subItems && (
                        <ul className="mt-3 space-y-1 list-disc list-inside text-slate-600 dark:text-slate-300">
                          {item.subItems.map((sub) => (
                            <li key={sub}>{sub}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Special handling for Compliance section */}
              {section.paragraphs && (
                <div className="mt-6">
                  {section.paragraphs.map((para) => (
                    <p
                      key={para}
                      className="text-slate-600 dark:text-slate-300"
                    >
                      {para}
                    </p>
                  ))}
                  {section.listItems && (
                    <ul className="mt-4 space-y-2">
                      {section.listItems.map((li) => (
                        <li key={li} className="flex items-center gap-3">
                          <ShieldCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                          <span className="text-slate-600 dark:text-slate-300">
                            {li}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.footer && (
                    <p className="mt-6 text-slate-600 dark:text-slate-300">
                      {section.footer}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
